import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {projectStatus} from 'constants/enums';
/**
 * types for project data
 */

export type projectTeamMember = {
  id: number;
  first_name: string;
  last_name: string;
  emp_id: number;
  role: string;
  profile_photo_url: string;
  permissions: {
    id: number;
    name: string;
  }[];
};

export type Project = {
  id: number;
  proj_id: string | null;
  description: string;
  name: string;
  org_id?: number;
  is_active?: boolean;
  sprint_duration: number;
  end_date: Date;
  start_date: Date;
  team_member?: projectTeamMember[];
  status: keyof typeof projectStatus;
};

export type ProjectStateType = {
  isLoading: boolean;
  errorProjectState: boolean | string;
  successProjectState: boolean | string;
  selectedProjectData: {
    name: string;
    id: number | null;
    data: Project | null;
    team: projectTeamMember[];
  };
  totalProjectPages: number;
  currentProjectPage: number;
  projectsList: Project[];
  nextProjectPage: number | null;
  allProjects: Project[];
};

/**
 * get project payload and reponse types
 */
export type getProjectsRequestParams = {
  org_id: number;
  page?: number;
  search_text?: string;
  sort?: string;
  filter?: any[];
};

export type getProjectsSuccessResponse = {
  data: {
    projects: Project[];
    current_page: number;
    next_page: number | null;
    total_page: number;
  };
  status_code: string;
};

/**
 * add project payload and reponse types
 */
export type addProjectRequestPayload = Omit<
  Project,
  'id' | 'proj_id' | 'team_member' | 'start_date' | 'end_date' // Ommiting some field to redefine then with different data type
> & {team_member: number[]; start_date: string; end_date: string}; // start, end date must be ISO string,

export type addProjectSucessResponse = {
  data: Project;
  status_code: string;
};

/**
 * add member to project
 */
export type addEmployeeToProjectRequestPayload = {
  proj_id: number;
  employee_id: number;
};

export type addEmployeeToProjectSuccessResponse = {
  status_code: string;
  data: {
    id: number,
    proj_id: string,
    employees: projectTeamMember[];
  };
};
/**
 * update project payload and reponse types
 */
export type updateProjectRequestPayload = Pick<
  Project,
  'id' | 'name' | 'description' | 'start_date' | 'end_date' | 'sprint_duration'
> & {start_date: string; end_date: string}; // start, end date must be ISO string,

export type updateProjectSucessResponse = {
  data: Omit<Project, 'org_id' | 'is_active' | 'team_member'>;
  status_code: string;
};

/**
 * get project details type
 */
export type getProjectDetailsParams = {
  id: number;
};
export type getProjectDetailsSuccessResponse = {
  status_code: string;
  data: Omit<Project, 'team_member' | 'is_active' | 'org_id'> & {
    start_date: string;
    end_date: string;
  };
};

/**
 * get project team members
 */
export type getProjectMemberParams = {
  proj_id: number;
};

export type getProjectMemberSuccessResponse = {
  status_code: string;
  data: {
    int: number;
    proj_id: number;
    employees: projectTeamMember[];
  };
};

const initialState: ProjectStateType = {
  isLoading: false,
  errorProjectState: false,
  successProjectState: false,
  selectedProjectData: {
    id: null,
    data: null,
    team: [],
    name: '',
  },
  totalProjectPages: 0,
  currentProjectPage: 0,
  projectsList: [],
  nextProjectPage: null,
  allProjects:[],
};

const ProjectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    getProjectsRequest(state, action: PayloadAction<getProjectsRequestParams>) {
      state.isLoading = true;
      state.errorProjectState = false;
      state.successProjectState = false;
      action.payload?.page === 1 && (state.allProjects = []);
    },
    getProjectsSuccess(
      state,
      action: PayloadAction<getProjectsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.currentProjectPage = action.payload.data.current_page;
      state.totalProjectPages = action.payload.data.total_page;
      state.nextProjectPage = action.payload.data.next_page;
      state.projectsList = action.payload.data.projects;

      // keep concatinating the list at the end, the filter
      // data will not be added into this since those dropdown
      // are on the other screen and on those screen data will be
      // fetched from beggining
      if (action.payload.data.current_page === 1) {
        state.allProjects = action.payload.data.projects;
      } else {
        let allProjects = [...state.allProjects];
        allProjects = allProjects.concat(action.payload.data.projects);
        state.allProjects = allProjects;
      }

    },
    getProjectsFailure(state, action: PayloadAction<string | boolean>) {
      state.errorProjectState = action.payload;
      state.isLoading = false;
    },

    addProjectRequest(state, action: PayloadAction<addProjectRequestPayload>) {
      state.isLoading = true;
      state.errorProjectState = false;
      state.successProjectState = false;
    },
    addProjectSuccess(state, action: PayloadAction<addProjectSucessResponse>) {
      state.isLoading = false;
      state.successProjectState = action.payload.status_code;
    },
    addProjectFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorProjectState = action.payload;
    },

    setCurrentSelectedProject(state, action: PayloadAction<number>) {
      state.selectedProjectData.id = action.payload;
    },
    setProjectName(state, action: PayloadAction<string>) {
      state.selectedProjectData.name = action.payload;
    },

    // temporary used for setting project team member
    setCurrentSelectedProjectTeam(
      state,
      action: PayloadAction<projectTeamMember[]>,
    ) {
      state.selectedProjectData.team = action.payload;
    },

    addEmployeeToProjectRequest(
      state,
      action: PayloadAction<addEmployeeToProjectRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorProjectState = false;
      state.successProjectState = false;
    },
    addEmployeeToProjectFailure(
      state,
      action: PayloadAction<string | boolean>,
    ) {
      state.isLoading = false;
      state.errorProjectState = action.payload;
    },
    addEmployeeToProjectSuccess(
      state,
      action: PayloadAction<addEmployeeToProjectSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successProjectState = action.payload.status_code;
    },
    updateProjectRequest(
      state,
      action: PayloadAction<updateProjectRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorProjectState = false;
      state.successProjectState = false;
    },
    updateProjectSuccess(
      state,
      action: PayloadAction<updateProjectSucessResponse>,
    ) {
      state.isLoading = false;
      state.successProjectState = action.payload.status_code;
      state.selectedProjectData.name = action.payload.data.name;
    },
    updateProjectFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorProjectState = action.payload;
    },

    getProjectDetailsRequest(
      state,
      action: PayloadAction<getProjectDetailsParams>,
    ) {
      state.isLoading = true;
      state.errorProjectState = false;
    },
    getProjectDetailsSuccess(
      state,
      action: PayloadAction<getProjectDetailsSuccessResponse>,
    ) {
      state.isLoading = false;
      state.selectedProjectData.data = action.payload.data;
    },
    getProjectDetailsFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorProjectState = action.payload;
    },

    getProjectTeamMemberRequest(
      state,
      action: PayloadAction<getProjectMemberParams>,
    ) {
      state.isLoading = true;
      state.errorProjectState = false;
      state.selectedProjectData.team = [];
    },
    getProjectTeamMemberFailure(
      state,
      action: PayloadAction<string | boolean>,
    ) {
      state.isLoading = false;
    },
    getProjectTeamMemberSuccess(
      state,
      action: PayloadAction<getProjectMemberSuccessResponse>,
    ) {
      state.isLoading = false;
      state.selectedProjectData.team = action.payload.data.employees;
    },

    resetMessage(state) {
      state.errorProjectState = false;
      state.successProjectState = false;
    },
  },
});

export default ProjectSlice.reducer;
export const {
  getProjectsFailure,
  getProjectsRequest,
  getProjectsSuccess,
  resetMessage,
  addProjectFailure,
  addProjectRequest,
  addProjectSuccess,
  setCurrentSelectedProject,
  addEmployeeToProjectFailure,
  addEmployeeToProjectRequest,
  addEmployeeToProjectSuccess,
  updateProjectRequest,
  updateProjectSuccess,
  updateProjectFailure,
  getProjectDetailsRequest,
  getProjectDetailsSuccess,
  getProjectDetailsFailure,
  setCurrentSelectedProjectTeam,
  getProjectTeamMemberFailure,
  getProjectTeamMemberRequest,
  getProjectTeamMemberSuccess,setProjectName,
} = ProjectSlice.actions;
